import PropTypes from "prop-types";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useFormContext, useWatch } from "react-hook-form";
import startCase from "lodash/startCase";
import { toast } from "sonner";
import { z } from "zod";

import {
  BaseForm,
  FormInput,
  FormMultiSelect,
  FormSelect,
} from "components/BaseForm";
import { useCustomFieldGroupsMutations } from "hooks/useCustomFieldGroupsTable";
import CustomFieldService from "services/CustomField";

const schema = z.object({
  name: z.string().trim().min(1, "Name is required"),
  modelType: z.string().nullable().refine(Boolean, "Model Type is required"),
  customFieldConfigs: z
    .array(z.string())
    .min(1, "Select at least one custom field"),
  description: z.string(),
});

const fetchModelTypes = async () => {
  const modelTypes = await CustomFieldService.getCustomFieldConfigModelTypes();
  return modelTypes.map(modelType => ({
    key: modelType,
    value: modelType,
    text: startCase(modelType),
  }));
};

const fetchCustomFieldConfigs = async modelType => {
  const configs =
    await CustomFieldService.getCustomFieldConfigsForModel(modelType);
  return configs.map(config => ({
    key: config.id,
    value: config.label,
    text: config.label,
  }));
};

const CustomFieldGroupFormBody = ({ modelTypeOptions }) => {
  const { setValue } = useFormContext();
  const modelType = useWatch({ name: "modelType" });

  const { data: customFieldOptions = [] } = useQuery({
    queryKey: ["custom-field-configs-for-model", modelType],
    queryFn: () => fetchCustomFieldConfigs(modelType),
    enabled: !!modelType,
  });

  // Field labels are scoped to a model, so any prior selection is meaningless
  // once the model changes.
  const handleModelTypeChange = () => {
    setValue("customFieldConfigs", [], { shouldValidate: true });
  };

  return (
    <>
      <FormInput name="name" label="Name" required />
      <FormSelect
        name="modelType"
        label="Model Type"
        placeholder="Select Model Type"
        required
        options={modelTypeOptions}
        onValueChange={handleModelTypeChange}
      />
      <FormMultiSelect
        name="customFieldConfigs"
        label="Custom Fields"
        placeholder="Select Custom Fields"
        required
        options={customFieldOptions}
        disabled={!modelType}
      />
      <FormInput name="description" label="Description" />
    </>
  );
};

CustomFieldGroupFormBody.propTypes = {
  modelTypeOptions: PropTypes.array.isRequired,
};

const CustomFieldGroupForm = ({ model, onSuccess }) => {
  const isEdit = !!model?.id;
  const { create, update } = useCustomFieldGroupsMutations();

  const { data: modelTypeOptions = [] } = useQuery({
    queryKey: ["custom-field-config-model-types"],
    queryFn: fetchModelTypes,
  });

  const defaults = useMemo(
    () => ({
      name: model?.name ?? "",
      modelType: model?.model_type ?? null,
      customFieldConfigs: (model?.custom_field_configs ?? []).map(
        cfc => cfc.label
      ),
      description: model?.description ?? "",
    }),
    [model]
  );

  const handleSubmit = async values => {
    if (isEdit) {
      await update.mutateAsync({ id: model.id, ...values });
    } else {
      await create.mutateAsync(values);
    }
  };

  const handleSuccess = () => {
    toast.success(
      isEdit ? "Custom field group updated" : "Custom field group created"
    );
    onSuccess?.();
  };

  return (
    <BaseForm
      schema={schema}
      defaultValues={defaults}
      onSubmit={handleSubmit}
      onSuccess={handleSuccess}
      submitButtonText="Save"
    >
      <CustomFieldGroupFormBody modelTypeOptions={modelTypeOptions} />
    </BaseForm>
  );
};

CustomFieldGroupForm.propTypes = {
  model: PropTypes.object,
  onSuccess: PropTypes.func,
};

export default CustomFieldGroupForm;
